/**
 * Option Selector - Range Slider Component
 * Handles the interactive range slider for product option selection
  */

import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

class OptionSelectors extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.rangeSliders = this.querySelectorAll('[range-slider]');
    this.detailedSelects = this.querySelectorAll('[detailed-select]');
    this.optionRadios = this.querySelectorAll('.option-radio input[type="radio"]');

    // Initialize range sliders
    this.rangeSliders.forEach(slider => {
      this.setupRangeSlider(slider);
    });

    // Initialize detailed selects
    this.detailedSelects.forEach(select => {
      this.setupDetailedSelect(select);
    });

    // Initialize radio buttons
    this.optionRadios.forEach(radio => {
      this.setupOptionRadio(radio);
    });

    this.setupGiftCard();
    this.setupHelpButton();
    this.setupPlusMore();
  }

  setupPlusMore() {
    const plusText = this.querySelectorAll('.plus-more');
    plusText.forEach(plus => {
      plus.addEventListener('click', () => {
        const optionContainer = plus.closest('.option-container');
        if (optionContainer) {
          const select = optionContainer.querySelector('select');
          if (select) {
            select.focus();
            select.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      });
    });
  }

  setupRangeSlider(slider) {

    console.log('💥💥💥 RANGE SLIDER:: ', slider);

    const parent = slider.closest('.option-range');
    if (!parent) return;

    const optionPosition = slider.dataset.optionPosition;
    const select = this.querySelector(`select[data-option-position="${optionPosition}"]`);
    const thumbContainer = parent.querySelector('.thumb-overlay');
    const steps = parent.querySelectorAll('.step');

    // Initial UI update
    this.updateSliderUI(slider, thumbContainer, steps, parseInt(slider.value));

    // Update on slider input
    slider.addEventListener('input', (e) => {
      const value = parseInt(e.target.value);
      this.updateSliderUI(slider, thumbContainer, steps, value);
      this.updateSelectFromSlider(slider, select, value);
    });

    // Update on slider change (final selection)
    slider.addEventListener('change', (e) => {
      const value = parseInt(e.target.value);
      this.updateSelectFromSlider(slider, select, value, true);
    });
  }

  updateSliderUI(slider, thumbContainer, steps, value) {
    const min = parseInt(slider.min) || 1;
    const max = parseInt(slider.max) || 100;
    const percentage = ((value - min) / (max - min)) * 100;

    // Update thumb overlay position
    if (thumbContainer) {
      const offset = value > 9 ? 0 : 1;
      let leftPosition = `${percentage - offset}%`;

      if (value === 1) {
        leftPosition = `calc(${leftPosition} + 10px)`;
      }

      thumbContainer.style.left = leftPosition;
      thumbContainer.textContent = value;

      // Highlight special values
      if ([4, 8, 12].includes(value)) {
        thumbContainer.classList.add('highlight');
      } else {
        thumbContainer.classList.remove('highlight');
      }
    }

    // Update step colors
    steps.forEach((step, index) => {
      const stepNumber = index + 1;
      if (stepNumber <= value - min + 1) {
        // step.style.background = 'rgb(var(--color-foreground))';
        step.style.background = '#000';

      } else {
        step.style.background = '#DBDBDB';
      }
    });

    // Update background gradient of the slider itself via CSS variable
    slider.style.setProperty('--range-progress', `${percentage}%`);
  }

  updateSelectFromSlider(currentSlider, select, value, triggerChange = false) {
    if (!select) return;

    // Update all other sliders for the same option position
    const optionPosition = currentSlider.dataset.optionPosition;
    const rangeSliders = this.querySelectorAll(`[range-slider][data-option-position="${optionPosition}"]`);

    rangeSliders.forEach(slider => {
      if (slider !== currentSlider) {
        slider.value = value;
        const parent = slider.closest('.option-range');
        if (parent) {
          const thumbContainer = parent.querySelector('.thumb-overlay');
          const steps = parent.querySelectorAll('.step');
          this.updateSliderUI(slider, thumbContainer, steps, value);
        }
      }
    });

    const options = Array.from(select.options);
    const matchingOption = options.find(option => {
      const hooksRaw = option.dataset.hooks;
      if (!hooksRaw) return false;

      let hooks;
      try {
        hooks = [].concat(JSON.parse(hooksRaw));
      } catch (e) {
        hooks = [parseInt(hooksRaw)];
      }

      return hooks.includes(value);
    });

    if (matchingOption && select.value !== matchingOption.value) {
      select.value = matchingOption.value;

      // Update variant info in real-time
      this.updateVariantInfo();

      if (triggerChange) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  setupDetailedSelect(select) {
    select.addEventListener('change', (e) => {
      const selectedOption = e.target.options[e.target.selectedIndex];
      this.updateRangeSliderFromSelect(select, selectedOption);
      this.updateVariantInfo();
    });
  }

  updateRangeSliderFromSelect(select, selectedOption) {
    const optionPosition = select.dataset.optionPosition;
    const rangeSliders = this.querySelectorAll(`[range-slider][data-option-position="${optionPosition}"]`);

    rangeSliders.forEach(rangeSlider => {
      if (selectedOption.dataset.hooks) {
        let hooks;
        try {
          hooks = [].concat(JSON.parse(selectedOption.dataset.hooks));
        } catch (e) {
          hooks = [parseInt(selectedOption.dataset.hooks)];
        }

        if (hooks.length > 0) {
          rangeSlider.value = hooks[0];
          const parent = rangeSlider.closest('.option-range');
          if (parent) {
            const thumbContainer = parent.querySelector('.thumb-overlay');
            const steps = parent.querySelectorAll('.step');
            this.updateSliderUI(rangeSlider, thumbContainer, steps, hooks[0]);
          }
        }
      }
    });
  }

  setupOptionRadio(radio) {
    radio.addEventListener('change', () => {
      this.updateVariantInfo();
    });
  }

  setupGiftCard() {
    const giftCheckbox = this.querySelector('[show_hide_gift_card_fields]');
    if (giftCheckbox) {
      giftCheckbox.addEventListener('change', (e) => {
        const giftCardFields = document.querySelector('.gift-card-recipient-form');
        if (giftCardFields) {
          giftCardFields.style.display = e.target.checked ? 'block' : 'none';
        }
        this.updateVariantInfo();
      });
    }
  }

  setupHelpButton() {
    const helpBtn = this.querySelector('[data-modal-trigger="help-me-choose"]');
    if (helpBtn) {
      helpBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const modal = document.getElementById('st-help-me-choose-modal');
        if (modal && typeof modal.showDialog === 'function') {
          modal.showDialog();
        }
      });
    }
  }

  updateVariantInfo() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);

    if (variant) {
      this.updateHiddenVariantInput(variant.id);
      this.updateURL(variant.id);

      // Update local display if elements exist
      const priceEl = this.querySelector('.variant-price span');
      if (priceEl) {
        priceEl.textContent = variant.price_html;
      }

      const storageEl = this.querySelector('.storage');
      if (storageEl) {
        storageEl.innerHTML = variant.storage_html;
      }

      const railLengthEl = this.querySelector('.rail-length span');
      if (railLengthEl) {
        railLengthEl.innerHTML = variant.rail_length_html;
      }

      // Dispatch event for theme-wide updates
      this.dispatchEvent(new CustomEvent('variant-change', {
        bubbles: true,
        composed: true,
        detail: { variant: variant }
      }));

      // Dispatch theme-standard event
      const productId = this.closest('[data-product-id]')?.dataset.productId;
      if (productId) {
        document.dispatchEvent(new VariantUpdateEvent(variant, this.id || 'option-selectors', {
          productId: productId,
          html: document // Pass document as a placeholder if HTML fragment isn't needed for morphing
        }));
      }
    }
  }

  getSelectedOptions() {
    const options = [];
    this.querySelectorAll('select').forEach(select => {
      options.push(select.value);
    });
    this.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
      options.push(radio.value);
    });
    return options;
  }

  findVariant(selectedOptions) {
    const variantDataEl = this.querySelector('#variant-data');
    if (!variantDataEl) return null;

    const variantData = JSON.parse(variantDataEl.textContent);
    return variantData.find(variant => {
      return variant.options.every(option => selectedOptions.includes(option));
    });
  }

  updateURL(variantId) {
    if (!variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({ path: url.href }, '', url.href);
  }

  updateHiddenVariantInput(variantId) {
    const parent = this.closest('.quick-add-modal') || this.closest('.product-information') || this.closest('product-card');
    const form = parent ? parent.querySelector('form[action*="/cart/add"]') : this.closest('form');

    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        input.value = variantId;
        // Also trigger change event on the hidden input to notify other listeners
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

// Register custom element
if (!customElements.get('option-selectors')) {
  customElements.define('option-selectors', OptionSelectors);
}
